import { useEffect, useState } from "react";
import { LoadPanel } from "devextreme-react";
import notify from "devextreme/ui/notify";
import { useTranslation } from "react-i18next";
import {
    getSurveyStats, getAllSurveys, SurveyWithTicket,
} from "../../../api/support";
import "../dashboard/SupportDashboard.css";

const PRODUCT_NAME: Record<number, string> = {
    10: "mobiOffice", 11: "mobiSales", 12: "mobiShop", 14: "mobiRoute",
};
const PRODUCT_COLOR: Record<number, string> = {
    10: "#6366F1", 11: "#8B5CF6", 12: "#EC4899", 14: "#14B8A6",
};

const SCORE_COLOR: Record<number, string> = {
    1: "#EF4444", 2: "#F97316", 3: "#EAB308", 4: "#22C55E", 5: "#10B981",
};
const SCORE_BG: Record<number, string> = {
    1: "bg-red-50", 2: "bg-orange-50", 3: "bg-yellow-50", 4: "bg-green-50", 5: "bg-emerald-50",
};
const SCORE_LABEL: Record<number, string> = {
    1: "surveyVeryBad", 2: "surveyBad", 3: "surveyOkay", 4: "surveyGood", 5: "surveyExcellent",
};

const STATUS_TEXT: Record<number, string> = {
    0: "statusNewOpened", 1: "statusAnswered", 2: "statusCustomerReplied", 3: "statusPending", 4: "statusClosed",
};
const STATUS_COLOR_HEX: Record<number, string> = {
    0: "#3B82F6", 1: "#22C55E", 2: "#F97316", 3: "#EAB308", 4: "#6B7280",
};
// Schema: 1=high, 2=medium, 3=low
const PRIORITY_TEXT: Record<number, string> = {
    1: "priorityHigh", 2: "priorityNormal", 3: "priorityLow",
};
const PRIORITY_COLOR_HEX: Record<number, string> = {
    1: "#EF4444", 2: "#FB923C", 3: "#9CA3AF",
};
const PRIORITY_ICON: Record<number, string> = { 1: "⚡", 2: "↑", 3: "↓" };

interface SurveyStatsData {
    total: number;
    responded: number;
    avgScore: number;
    distribution: Record<number, number>;
}

function pct(part: number, total: number) {
    return total === 0 ? 0 : Math.round((part / total) * 100);
}

function formatDate(date: string) {
    return new Date(date).toLocaleDateString("tr-TR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
}

function Stars({ score, size = "w-4 h-4" }: { score: number; size?: string }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
                <svg key={s} className={`${size} ${s <= score ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
            ))}
        </div>
    );
}

/* ── Detail Popup ─────────────────────────────────────────────────────────── */
function SurveyDetailPopup({ survey, onClose }: { survey: SurveyWithTicket; onClose: () => void }) {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.45)" }} onClick={onClose}>
            <div
                className="rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
                style={{ background: "var(--base-bg)", border: "1px solid var(--border-color)" }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header - Puan */}
                <div className="px-6 pt-6 pb-4 text-center" style={{ borderBottom: "1px solid var(--border-color)" }}>
                    <Stars score={survey.score} size="w-8 h-8" />
                    <p className="text-2xl font-bold mt-2" style={{ color: SCORE_COLOR[survey.score] }}>
                        {survey.score}/5 — {t(SCORE_LABEL[survey.score])}
                    </p>
                    <p className="text-xs mt-1" style={{ color: "var(--subtitle-text-color)" }}>
                        {formatDate(survey.created_at)}
                    </p>
                </div>

                {/* Yorum */}
                {survey.feedback && (
                    <div className="px-6 py-4" style={{ borderBottom: "1px solid var(--border-color)" }}>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--subtitle-text-color)" }}>{t("surveyFeedbackLabel")}</label>
                        <p className="text-sm leading-relaxed" style={{ color: "var(--base-text-color)" }}>
                            "{survey.feedback}"
                        </p>
                    </div>
                )}

                {/* Ticket Bilgileri */}
                <div className="px-6 py-4 space-y-3" style={{ borderBottom: "1px solid var(--border-color)" }}>
                    <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--subtitle-text-color)" }}>{t("ticketInfo")}</label>

                    <div className="flex items-center gap-2">
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)", color: "var(--subtitle-text-color)" }}>
                            {survey.ticket_code}
                        </span>
                        <span className="text-xs font-semibold" style={{ color: PRODUCT_COLOR[survey.product_code] ?? "#6B7280" }}>
                            {PRODUCT_NAME[survey.product_code] ?? ""}
                        </span>
                    </div>

                    <p className="text-sm font-semibold" style={{ color: "var(--base-text-color)" }}>{survey.ticket_title}</p>

                    {survey.ticket_description && (
                        <div className="text-xs leading-relaxed rounded p-2" style={{ background: "var(--background-gray-color)", color: "var(--base-text-color-alpha)" }}
                            dangerouslySetInnerHTML={{ __html: survey.ticket_description }}
                        />
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("status")}</label>
                            <span className="text-xs font-semibold flex items-center gap-1" style={{ color: STATUS_COLOR_HEX[survey.ticket_status] }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR_HEX[survey.ticket_status] }} />
                                {t(STATUS_TEXT[survey.ticket_status])}
                            </span>
                        </div>
                        <div>
                            <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("priority")}</label>
                            <span className="text-xs font-semibold" style={{ color: PRIORITY_COLOR_HEX[survey.ticket_priority] }}>
                                {PRIORITY_ICON[survey.ticket_priority]} {t(PRIORITY_TEXT[survey.ticket_priority])}
                            </span>
                        </div>
                        <div>
                            <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("customer")}</label>
                            <span className="text-xs font-medium" style={{ color: "var(--base-text-color)" }}>{survey.customer_name || "—"}</span>
                            {survey.customer_email && (
                                <span className="block text-xs" style={{ color: "var(--subtitle-text-color)" }}>{survey.customer_email}</span>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("assignedTo")}</label>
                            <span className="text-xs font-medium" style={{ color: "var(--base-text-color)" }}>{survey.assigned_to_name || "—"}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {survey.ticket_created_at && (
                            <div>
                                <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("createdAt")}</label>
                                <span className="text-xs" style={{ color: "var(--base-text-color-alpha)" }}>{formatDate(survey.ticket_created_at)}</span>
                            </div>
                        )}
                        {survey.closed_at && (
                            <div>
                                <label className="block text-xs mb-0.5" style={{ color: "var(--subtitle-text-color)" }}>{t("closed")}</label>
                                <span className="text-xs" style={{ color: "var(--base-text-color-alpha)" }}>{formatDate(survey.closed_at)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Kapat */}
                <div className="px-6 py-4 flex justify-end" style={{ background: "var(--base-bg-darken-4)" }}>
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-xs rounded-xl font-semibold transition-colors"
                        style={{ background: "var(--background-gray-color)", color: "var(--base-text-color)" }}
                    >
                        {t("close")}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Main Page ────────────────────────────────────────────────────────────── */

export default function SurveysPage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState<SurveyStatsData | null>(null);
    const [surveys, setSurveys] = useState<SurveyWithTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterScore, setFilterScore] = useState<number | null>(null);
    const [selectedSurvey, setSelectedSurvey] = useState<SurveyWithTicket | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [statsData, surveysData] = await Promise.all([getSurveyStats(), getAllSurveys()]);
                setStats(statsData);
                setSurveys(surveysData);
            } catch {
                notify(t("surveyLoadError"), "error", 2000);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filteredSurveys = filterScore
        ? surveys.filter(s => s.score === filterScore)
        : surveys;

    const responseRate = stats ? pct(stats.responded, stats.total) : 0;

    return (
        <div className="flex flex-col h-full w-full overflow-auto" style={{ background: "var(--background-color)" }}>
            <LoadPanel visible={loading} />

            <div className="p-6 space-y-5">

                {/* KPI Kartlari */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="dash-card p-5">
                        <p className="text-xs font-semibold dash-text-muted uppercase tracking-wider mb-3">{t("surveyAvgScore")}</p>
                        <div className="flex items-center gap-3">
                            <span className="text-4xl font-bold" style={{ color: "var(--base-text-color)" }}>
                                {stats?.avgScore ?? "—"}
                            </span>
                            <div>
                                <Stars score={Math.round(stats?.avgScore ?? 0)} size="w-5 h-5" />
                                <p className="text-xs dash-text-muted mt-1">{t("surveyOutOf5")}</p>
                            </div>
                        </div>
                    </div>

                    <div className="dash-card p-5">
                        <p className="text-xs font-semibold dash-text-muted uppercase tracking-wider mb-3">{t("surveyTotalResponses")}</p>
                        <p className="text-4xl font-bold" style={{ color: "var(--base-text-color)" }}>{stats?.responded ?? 0}</p>
                        <p className="text-xs dash-text-muted mt-1">{t("surveyOfClosed", { total: stats?.total ?? 0 })}</p>
                    </div>

                    <div className="dash-card p-5">
                        <p className="text-xs font-semibold dash-text-muted uppercase tracking-wider mb-3">{t("surveyResponseRate")}</p>
                        <p className="text-4xl font-bold" style={{ color: "var(--base-text-color)" }}>%{responseRate}</p>
                        <div className="mt-2 h-2 dash-progress-bg rounded-full overflow-hidden">
                            <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${responseRate}%` }} />
                        </div>
                    </div>

                    <div className="dash-card p-5">
                        <p className="text-xs font-semibold dash-text-muted uppercase tracking-wider mb-3">{t("surveySatisfied")}</p>
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold text-green-600">
                                {stats ? (stats.distribution[4] ?? 0) + (stats.distribution[5] ?? 0) : 0}
                            </span>
                            <span className="text-lg dash-text-muted">/</span>
                            <span className="text-xl font-bold text-red-500">
                                {stats ? (stats.distribution[1] ?? 0) + (stats.distribution[2] ?? 0) : 0}
                            </span>
                        </div>
                        <p className="text-xs dash-text-muted mt-1">{t("surveySatisfiedVsUnsatisfied")}</p>
                    </div>
                </div>

                {/* Yildiz Dagilimi + Yanit Listesi */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

                    {/* Sol - Yildiz Dagilimi */}
                    <div className="dash-card p-5 lg:sticky lg:top-6 self-start">
                        <h3 className="text-sm font-semibold dash-text-primary mb-4">{t("surveyScoreDistribution")}</h3>
                        <div className="space-y-3">
                            {[5, 4, 3, 2, 1].map(star => {
                                const count = stats?.distribution[star] ?? 0;
                                const percent = pct(count, stats?.responded ?? 0);
                                const isActive = filterScore === star;
                                return (
                                    <button
                                        key={star}
                                        onClick={() => setFilterScore(isActive ? null : star)}
                                        className={`w-full flex items-center gap-2.5 text-xs p-2 rounded-xl transition-all ${isActive ? "ring-2 ring-amber-400 bg-amber-50" : "hover:bg-gray-50"}`}
                                    >
                                        <span className="w-4 font-bold dash-text-secondary">{star}</span>
                                        <svg className="w-4 h-4 text-amber-400 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                        </svg>
                                        <div className="flex-1 h-2.5 dash-progress-bg rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-500"
                                                style={{ width: `${percent}%`, backgroundColor: SCORE_COLOR[star] }}
                                            />
                                        </div>
                                        <span className="w-8 text-right font-semibold dash-text-secondary">{count}</span>
                                        <span className="w-10 text-right dash-text-muted">%{percent}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {filterScore && (
                            <button
                                onClick={() => setFilterScore(null)}
                                className="mt-4 w-full text-xs text-center py-2 rounded-xl font-medium transition-colors"
                                style={{ background: "var(--background-gray-color)", color: "var(--subtitle-text-color)" }}
                            >
                                {t("surveyShowAll")}
                            </button>
                        )}
                    </div>

                    {/* Sag - Anket Listesi */}
                    <div className="lg:col-span-3 dash-card p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold dash-text-primary">
                                {t("surveyResponses")}
                                {filterScore && (
                                    <span className="ml-2 text-xs font-normal dash-text-muted">
                                        ({filterScore} {t("surveyStar")} — {filteredSurveys.length} {t("surveyResult")})
                                    </span>
                                )}
                            </h3>
                            <span className="text-xs dash-text-muted dash-badge px-2.5 py-1 rounded-lg">
                                {filteredSurveys.length} {t("surveyResult")}
                            </span>
                        </div>

                        {filteredSurveys.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 dash-text-muted">
                                <svg className="w-12 h-12 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                                <p className="text-sm font-medium">{t("noSurveyData")}</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredSurveys.map(s => (
                                    <div
                                        key={s.ref}
                                        onClick={() => setSelectedSurvey(s)}
                                        className={`rounded-xl p-4 border transition-all hover:shadow-md cursor-pointer ${SCORE_BG[s.score]}`}
                                        style={{ borderColor: SCORE_COLOR[s.score] + "30" }}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.05)", color: "var(--subtitle-text-color)" }}>
                                                        {s.ticket_code}
                                                    </span>
                                                    <span className="text-xs font-semibold" style={{ color: PRODUCT_COLOR[s.product_code] ?? "#6B7280" }}>
                                                        {PRODUCT_NAME[s.product_code] ?? ""}
                                                    </span>
                                                </div>
                                                <p className="text-sm font-medium truncate" style={{ color: "var(--base-text-color)" }}>
                                                    {s.ticket_title}
                                                </p>

                                                {s.feedback && (
                                                    <div className="mt-2 flex items-start gap-2">
                                                        <svg className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--subtitle-text-color)" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                        </svg>
                                                        <p className="text-xs leading-relaxed" style={{ color: "var(--base-text-color-alpha)" }}>
                                                            "{s.feedback}"
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-3 mt-2.5 text-xs" style={{ color: "var(--subtitle-text-color)" }}>
                                                    {s.customer_name && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                            {s.customer_name}
                                                        </span>
                                                    )}
                                                    {s.assigned_to_name && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                            </svg>
                                                            {s.assigned_to_name}
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        {formatDate(s.created_at)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-center shrink-0">
                                                <Stars score={s.score} />
                                                <span className="text-lg font-bold mt-1" style={{ color: SCORE_COLOR[s.score] }}>
                                                    {s.score}/5
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Detail Popup */}
            {selectedSurvey && (
                <SurveyDetailPopup
                    survey={selectedSurvey}
                    onClose={() => setSelectedSurvey(null)}
                />
            )}
        </div>
    );
}
